import {
  type JiraIssueLinkByIdLookup,
  type JiraIssueLinkRecord,
} from './jira-service-types.ts';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isIssueReference = (value: unknown): value is { readonly key: string } =>
  isRecord(value) && isNonEmptyString(value.key);

const isIssueLinkRecord = (value: unknown): value is JiraIssueLinkRecord => {
  if (!isRecord(value) || !isNonEmptyString(value.id) || !isRecord(value.type)) {
    return false;
  }

  const { type, inwardIssue, outwardIssue } = value;
  if (
    !isNonEmptyString(type.id) ||
    !isNonEmptyString(type.name) ||
    !isNonEmptyString(type.inward) ||
    !isNonEmptyString(type.outward)
  ) {
    return false;
  }

  if (
    (inwardIssue !== undefined && !isIssueReference(inwardIssue)) ||
    (outwardIssue !== undefined && !isIssueReference(outwardIssue))
  ) {
    return false;
  }

  return isIssueReference(inwardIssue) || isIssueReference(outwardIssue);
};

/**
 * Parses a `GET /issue/{key}?fields=issuelinks` response, keeping only links to
 * `targetIssueKey`. Returns `undefined` when any part of the payload is malformed so
 * callers cannot mistake a bad response for "no links".
 */
export const parseJiraIssueLinks = (
  response: unknown,
  targetIssueKey: string,
): readonly JiraIssueLinkRecord[] | undefined => {
  if (!isRecord(response) || !isRecord(response.fields)) {
    return undefined;
  }

  const { issuelinks } = response.fields;
  if (!Array.isArray(issuelinks) || !issuelinks.every(isIssueLinkRecord)) {
    return undefined;
  }

  return issuelinks.filter(
    (link) =>
      link.inwardIssue?.key === targetIssueKey ||
      link.outwardIssue?.key === targetIssueKey,
  );
};

/**
 * Parses a `GET /issueLink/{id}` response. Jira answers a missing or hidden link with its
 * `errorMessages` envelope rather than a link.
 */
export const parseJiraIssueLink = (response: unknown): JiraIssueLinkByIdLookup => {
  if (isIssueLinkRecord(response)) {
    return { status: 'found', link: response };
  }
  if (isRecord(response) && Array.isArray(response.errorMessages)) {
    return { status: 'not-found' };
  }
  return { status: 'invalid-response' };
};
